import { useQuery } from '@tanstack/react-query';
import { TOPICS } from '@content-platform/shared';
import { api, apiErrorMessage } from '../lib/api';

/**
 * 토픽 모니터링 페이지.
 *
 * GET /api/topics/warnings 응답을 표시:
 *   1. 미매핑 태그(임계 ≥5 글) — 토픽 승격 후보
 *   2. 토픽별 글 수 분포 — 매핑 정합성 시각 확인
 *   3. 매핑 테이블 reference — packages/shared/src/topics.ts 의 토픽 정의
 *
 * 데이터 소스: GitHub repo content/posts/*.md (worker가 caches.default 5분 캐시).
 * 운영자가 새 토픽 추가/매핑 보강 → packages/shared/src/topics.ts 수정 → 재배포.
 */
export default function Topics() {
  const warnings = useQuery({
    queryKey: ['topics', 'warnings', 5],
    queryFn: () => api.getTopicWarnings(5),
  });

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-black tracking-tight">토픽 모니터</h1>
        <p className="mt-1 text-sm text-slate-600">
          매핑 안 된 태그 + 토픽별 글 수 분포 + 매핑 테이블. 새 토픽 추가는
          <code className="mx-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono">packages/shared/src/topics.ts</code>
          수정 후 재배포.
        </p>
      </header>

      {warnings.isLoading && (
        <p className="text-sm text-slate-500">로딩 중...</p>
      )}

      {warnings.isError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          토픽 데이터 조회 실패 — {apiErrorMessage(warnings.error)}
        </div>
      )}

      {warnings.data && (
        <>
          {/* === Section 1: 미매핑 태그 === */}
          <section>
            <h2 className="text-sm font-bold tracking-wide text-slate-700 uppercase mb-2">
              매핑 안 된 태그 (≥{warnings.data.threshold}글)
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              총 {warnings.data.totalPosts}개 글에서 어떤 토픽에도 매핑되지 않은 태그 중
              임계값 이상 누적된 것. 토픽 승격 후보.
            </p>
            {warnings.data.unmappedTags.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                임계 이상의 미매핑 태그 없음 — 매핑이 잘 따라오고 있습니다.
              </p>
            ) : (
              <ul className="divide-y border rounded bg-white">
                {warnings.data.unmappedTags.map((u) => (
                  <li
                    key={u.tag}
                    className="flex items-baseline justify-between gap-3 px-3 py-2"
                  >
                    <span className="font-mono text-sm">{u.tag}</span>
                    <span className="text-xs tabular-nums text-slate-500">
                      {u.count}글
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* === Section 2: 토픽 분포 === */}
          <section>
            <h2 className="text-sm font-bold tracking-wide text-slate-700 uppercase mb-2">
              토픽별 글 수
            </h2>
            <ul className="divide-y border rounded bg-white">
              {warnings.data.topicDistribution.map((t) => (
                <li
                  key={t.slug}
                  className="flex items-baseline justify-between gap-3 px-3 py-2"
                >
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold">{t.displayName}</span>
                    <span className="font-mono text-xs text-slate-400">
                      {t.slug}
                    </span>
                  </div>
                  <span className="text-xs tabular-nums text-slate-500">
                    {t.count}글
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* === Section 3: 매핑 테이블 reference === */}
          <section>
            <h2 className="text-sm font-bold tracking-wide text-slate-700 uppercase mb-2">
              매핑 테이블
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              현재 각 토픽에 매핑된 source 태그들. 비교는 case-insensitive +
              공백/대시/언더스코어 정규화. 수정은
              <code className="mx-1 rounded bg-slate-100 px-1 text-[11px] font-mono">packages/shared/src/topics.ts</code>.
            </p>
            <div className="space-y-3">
              {TOPICS.map((topic) => (
                <div
                  key={topic.slug}
                  className="rounded border bg-white p-3"
                >
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-semibold">{topic.displayName}</span>
                    <span className="font-mono text-xs text-slate-400">
                      {topic.slug}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {topic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs font-mono text-slate-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
